import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { loadRemote } from '@module-federation/runtime';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';

const PaymentApp = React.lazy(() =>
	loadRemote('paynowRemote/PaymentApp').then((m) => {
		if (!m) throw new Error('Failed to load paynowRemote/PaymentApp');
		return m as { default: React.ComponentType };
	}),
);

const ErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
	<div style={{ padding: '2rem', textAlign: 'center' }} role='alert'>
		<h2>Something went wrong</h2>
		<p>This section is temporarily unavailable. Please try again.</p>
		<button onClick={onRetry} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
			Retry
		</button>
	</div>
);

/**
 * Loads the anonymous payment family (`/payment/*`) from the paynowRemote plugin.
 * These are external token-link entry points — no auth, no safe-exit link to the
 * portal (the user may have no portal session at all).
 */
const PaymentGateway: React.FC = () => {
	const handleRetry = () => globalThis.location.reload();

	return (
		<Sentry.ErrorBoundary
			fallback={<ErrorFallback onRetry={handleRetry} />}
			beforeCapture={(scope) => {
				scope.setTag('product', 'paynow');
				scope.setTag('module_federation', 'true');
				scope.setContext('paynow_remote', {
					remoteEntry: import.meta.env.VITE_PAYNOW_REMOTE_URL,
					exposedModule: './PaymentApp',
				});
			}}
		>
			<Suspense fallback={<FullPageLoadingSplash message='Loading...' loading />}>
				<PaymentApp />
			</Suspense>
		</Sentry.ErrorBoundary>
	);
};

export default PaymentGateway;
